"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Shield, Zap, TrendingUp, Building, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 relative overflow-hidden">
      {/* Subtle Wave Patterns */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg width=%22100%22 height=%22100%22 viewBox=%220 0 100 100%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cpath d=%22M0 50 Q25 25 50 50 T100 50%22 stroke=%22%23f59e0b%22 stroke-width=%221%22 fill=%22none%22 opacity=%220.1%22/%3E%3C/svg%3E')]"></div>
        <div className="absolute bottom-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg width=%22100%22 height=%22100%22 viewBox=%220 0 100 100%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cpath d=%22M0 50 Q25 75 50 50 T100 50%22 stroke=%22%23ef4444%22 stroke-width=%221%22 fill=%22none%22 opacity=%220.1%22/%3E%3C/svg%3E')]"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl animate-pulse"></div>
      </div>

      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="container max-w-7xl mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-foreground">
            About WightWay Connect
          </h1>
          <p className="text-xl text-foreground/80 mb-8 max-w-3xl mx-auto">
            We're on a mission to empower businesses with secure, scalable tools that streamline operations and foster meaningful client relationships. Founded by industry experts, WightWay Connect is built for teams that demand excellence.
          </p>
          <div className="flex justify-center">
            <Button size="lg" asChild variant="outline">
              <Link to="/features">Explore Features</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-foreground">Our Story</h2>
              <p className="text-lg text-foreground/80 mb-6 leading-relaxed">
                WightWay Connect was born from the real-world needs of the WightWay Island Business Partnership (IBP) — a consultancy dedicated to turning ideas into thriving businesses. Our parent company, LPIWuk LTD, was created to develop innovative solutions to challenges faced by entrepreneurs and growing teams.
              </p>
              <p className="text-lg text-foreground/80 mb-8 leading-relaxed">
                In 2025, WightWay IBP was founded to tackle one of the Isle of Wight's biggest issues: the shortage of high-paying jobs and limited opportunities for local talent. Out of that mission came WightWay Connect, our platform designed to provide practical digital tools that help businesses grow.
              </p>
              <p className="text-lg text-foreground/80 mb-8 leading-relaxed">
                Backed by years of consulting experience, we saw the need for a comprehensive client portal to streamline operations, support startups from concept to launch, and help established companies scale sustainably. WightWay Connect does exactly that — giving teams the ability to manage projects, track time, and collaborate seamlessly in one place.
              </p>
              <p className="text-lg text-foreground/80 mb-8 leading-relaxed">
                Today, WightWay Connect embodies our vision: combining innovation with user-centric design to empower businesses worldwide. We're proud to start from the Isle of Wight and extend our impact globally, helping more teams bridge the gap between vision and execution.
              </p>
              <div className="flex space-x-4">
                <Button asChild>
                  <Link to="/pricing">View Plans</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Building className="h-5 w-5" />
                    <span>LPIWuk LTD 2019</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground/80">Legal entity established in 2019, providing the foundation for WightWay initiatives.</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <TrendingUp className="h-5 w-5" />
                    <span>WightWay Brand 2025</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground/80">WightWay brand incepted in 2025, encompassing consulting and platform development to drive economic impact.</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Zap className="h-5 w-5" />
                    <span>WightWay Connect 2025</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground/80">Launched in 2025 as a dedicated client portal platform empowering teams worldwide.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-muted/50">
        <div className="container max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">Our Mission & Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="flex items-center justify-center space-x-2 mb-2">
                  <Zap className="h-5 w-5 text-primary" />
                  <span>Innovation</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 text-center">We pioneer cutting-edge solutions that anticipate your needs and drive your success.</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="flex items-center justify-center space-x-2 mb-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <span>Security</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 text-center">Your data is our priority. We build with security at the core of everything we do.</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="flex items-center justify-center space-x-2 mb-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  <span>Growth</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 text-center">We partner with you to scale sustainably, from startup to enterprise.</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="flex items-center justify-center space-x-2 mb-2">
                  <Users className="h-5 w-5 text-primary" />
                  <span>Excellence</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 text-center">Every feature, every interaction—designed for exceptional user experience.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Team (Placeholder) */}
      <section className="py-20">
        <div className="container max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">Meet the Team</h2>
          <div className="max-w-md mx-auto">
            <Card>
              <CardHeader className="text-center">
                <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-10 w-10 text-primary" />
                </div>
                <CardTitle>Louis</CardTitle>
                <CardDescription className="text-center">Founder, Director, and Lead Consultant</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-foreground/80">As the sole founder of WightWay Connect, Louis brings extensive experience in business consulting and technology integration. Passionate about creating practical solutions, he leads the vision to empower teams and foster economic growth on the Isle of Wight and beyond.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary/10 py-20">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
            Join the WightWay Revolution
          </h2>
          <p className="text-lg text-foreground/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            Ready to experience the future of business operations? Start your journey with WightWay Connect today.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button size="lg" asChild className="bg-primary text-primary-foreground">
              <Link to="/signup">Get Started (7-day money back guarantee)</Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/contact">
                <Mail className="mr-2 h-5 w-5" />
                Contact Sales
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;